"use client";

import { format } from "date-fns";
import {
  ArrowLeftRight,
  ArrowRight,
  Briefcase,
  CalendarDays,
  ListChecks,
  MapPin,
  Pencil,
  Users,
} from "lucide-react";
import { useRouter } from "next/navigation";
import { useCallback, useMemo, type ReactNode } from "react";

import { Container } from "@/components/layout/container";
import {
  OverlayPanel,
  OverlayPanelAnchor,
  OverlayPanelContent,
} from "@/components/overlay-panel";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import FlightSearchForm, {
  IFlightSearchSubmitHandler,
} from "@/feature/flight/search/components/form/flight-search-form";
import {
  refetch,
  toggleModifyBar,
} from "@/feature/flight/search/state/slices/flight-search-status.slice";
import { CABIN_TYPES } from "@/feature/flight/search/types/constants";
import {
  type IDecodedFlightSearch,
  type TTripType,
} from "@/feature/flight/search/types/search";

import { cn } from "@/lib/utils";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import FilterDisplayPopover from "../filters/filter-popover";
import { FilterSection } from "../filters/flight-filter";

interface FlightSearchRouteInfoProps {
  searchData: IDecodedFlightSearch | null;
}

const TRIP_TYPE_LABELS: Record<TTripType, string> = {
  oneway: "One-way",
  roundtrip: "Round-trip",
  multicity: "Multi-city",
};

function getCabinLabel(cabinCode?: string) {
  return (
    CABIN_TYPES.find((item) => item.value === cabinCode)?.label ?? "Economy"
  );
}

function toDate(value: unknown) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  if (typeof value === "string" || typeof value === "number") {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
}

function airportLabel(
  airport: IDecodedFlightSearch["flights"][number]["from"] | undefined,
  fallback: string,
) {
  if (!airport?.iata_code) return fallback;
  const city = airport.city_name || airport.name || airport.iata_code;
  return `${city} (${airport.iata_code.toUpperCase()})`;
}

function airportIata(
  airport: IDecodedFlightSearch["flights"][number]["from"] | undefined,
  fallback: string,
) {
  if (!airport?.iata_code) return fallback;
  return airport.iata_code.toUpperCase();
}

function getRouteEndpoints(searchData: IDecodedFlightSearch | null) {
  if (!searchData?.flights.length) {
    return { from: undefined, to: undefined };
  }

  const first = searchData.flights[0];
  if (searchData.tripType !== "multicity") {
    return { from: first?.from, to: first?.to };
  }

  const last = searchData.flights[searchData.flights.length - 1];
  return { from: first?.from, to: last?.to ?? first?.to };
}

export default function FlightSearchRouteInfo({
  searchData,
}: FlightSearchRouteInfoProps) {
  const router = useRouter();

  const dispatch = useAppDispatch();
  const { showModifyBar, isResponseEnd } = useAppSelector(
    (state) => state.flightSearchStatus,
  );

  const openModifyDrawer = useCallback(
    () => dispatch(toggleModifyBar(true)),
    [dispatch],
  );

  const setModifyDrawer = useCallback(
    (value: boolean) => dispatch(toggleModifyBar(value)),
    [dispatch],
  );

  const summary = useMemo(() => {
    const endpoints = getRouteEndpoints(searchData);
    const tripTypeKey = searchData?.tripType ?? "oneway";

    return {
      tripType: TRIP_TYPE_LABELS[tripTypeKey],
      isRoundTrip: tripTypeKey === "roundtrip",
      from: airportLabel(endpoints.from, "From"),
      to: airportLabel(endpoints.to, "To"),
      fromIata: airportIata(endpoints.from, "From"),
      toIata: airportIata(endpoints.to, "To"),
      dates: dateLabel(searchData),
      travellers: travellersLabel(searchData),
      cabin: getCabinLabel(searchData?.cabin),
    };
  }, [searchData]);

  const metaCards = useMemo(
    () => [
      {
        key: "dates",
        icon: <CalendarDays className="h-3.5 w-3.5 shrink-0" />,
        label: "Dates",
        value: summary.dates,
      },
      {
        key: "travellers",
        icon: <Users className="h-3.5 w-3.5 shrink-0" />,
        label: "Travelers",
        value: summary.travellers,
      },
      {
        key: "cabin",
        icon: <Briefcase className="h-3.5 w-3.5 shrink-0" />,
        label: "Cabin",
        value: summary.cabin,
      },
    ],
    [summary.cabin, summary.dates, summary.travellers],
  );

  const handleModifySearch = ({ searchParams }: IFlightSearchSubmitHandler) => {
    router.push(`/flights${searchParams}`, { scroll: false });
    dispatch(refetch(true));
    setModifyDrawer(false);
  };

  return (
    <>
      <OverlayPanel open={!!showModifyBar} onOpenChange={setModifyDrawer}>
        <section
          className={cn(
            "sticky top-(--header-height) z-40 bg-background",
            !showModifyBar && "shadow-md",
          )}
        >
          <div className="relative">
            <OverlayPanelAnchor hideWhenOpen>
              <Container className="py-3">
                <div className="flex w-full flex-col gap-2.5 transition-opacity duration-200 md:flex-row md:items-center md:justify-between">
                  <div className="flex w-full min-w-0 items-stretch gap-2 md:w-auto">
                    <div className="min-w-0 flex-1 md:flex-none">
                      <RouteCard
                        tripType={summary.tripType}
                        isRoundTrip={summary.isRoundTrip}
                        from={summary.from}
                        to={summary.to}
                        fromIata={summary.fromIata}
                        toIata={summary.toIata}
                        onClick={openModifyDrawer}
                      />
                    </div>

                    <Button
                      type="button"
                      variant="destructive"
                      onClick={openModifyDrawer}
                      aria-expanded={!!showModifyBar}
                      className="shrink-0 md:hidden"
                    >
                      <Pencil className="h-4 w-4" />
                      Modify
                    </Button>

                    <Drawer>
                      <DrawerTrigger asChild>
                        <Button variant="outline" className="ml-auto lg:hidden">
                          <ListChecks className="h-4 w-4" />
                        </Button>
                      </DrawerTrigger>
                      <DrawerContent className=" flex flex-col">
                        <DrawerHeader className="text-left">
                          <DrawerTitle className="flex items-center justify-between">
                            Flight Filter
                            <FilterDisplayPopover />
                          </DrawerTitle>
                        </DrawerHeader>

                        <div className="h-[90vh] overflow-y-auto">
                          <FilterSection />
                        </div>
                      </DrawerContent>
                    </Drawer>
                  </div>

                  <div className="flex w-full min-w-0 sm:gap-2  md:w-auto  md:items-center md:justify-end">
                    {metaCards.map((card) => (
                      <SummaryCard
                        key={card.key}
                        icon={card.icon}
                        value={card.value}
                        onClick={openModifyDrawer}
                        className="flex-1 justify-center sm:flex-none"
                      />
                    ))}

                    <Button
                      type="button"
                      variant="destructive"
                      onClick={openModifyDrawer}
                      aria-expanded={!!showModifyBar}
                      className="hidden md:flex"
                    >
                      <Pencil className="h-4 w-4" />
                      Modify
                    </Button>
                  </div>
                </div>
              </Container>
            </OverlayPanelAnchor>

            <OverlayPanelContent
              className={cn(
                "bg-background shadow-[0_8px_10px_-5px_rgba(0,0,0,0.1)]",
              )}
            >
              <Container className="py-7">
                <FlightSearchForm
                  initialData={searchData}
                  submitHandler={handleModifySearch}
                />
              </Container>
            </OverlayPanelContent>
            <div className="absolute w-full">
              {!isResponseEnd && (
                <div className="bar w-full">
                  <div></div>
                </div>
              )}
            </div>
          </div>
        </section>
      </OverlayPanel>
    </>
  );
}

function dateLabel(searchData: IDecodedFlightSearch | null) {
  if (!searchData) return "Pick dates";

  const dates = searchData.flights
    .map((flight) => toDate(flight.departDate))
    .filter((value): value is Date => Boolean(value));

  if (dates.length === 0) return "Pick dates";

  if (searchData.tripType === "roundtrip" && dates.length >= 2) {
    return `${format(dates[0], "MMM d")} - ${format(dates[1], "MMM d")}`;
  }

  if (searchData.tripType === "multicity" && dates.length > 1) {
    return `${format(dates[0], "MMM d")} - ${format(dates[dates.length - 1], "MMM d")} (${dates.length} legs)`;
  }

  return format(dates[0], "EEE, MMM d");
}

function travellersLabel(searchData: IDecodedFlightSearch | null) {
  if (!searchData) return "1 traveler";

  const { adults, children, infants } = searchData.passengers;
  const total = adults + children + infants;
  return `${total} traveler${total === 1 ? "" : "s"}`;
}

function SummaryCard({
  icon,
  value,
  onClick,
  className,
}: {
  icon: ReactNode;
  value: string;
  onClick: () => void;

  className?: string;
}) {
  return (
    <Button
      variant="ghost"
      type="button"
      onClick={onClick}
      className={cn(
        "min-w-0 border border-transparent bg-transparent px-3  hover:border-border/70 hover:bg-muted/70 ",
        className,
      )}
    >
      <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
        {icon}
      </span>
      <div className="min-w-0 text-left leading-tight">
        <p className="truncate text-sm font-semibold">{value}</p>
      </div>
    </Button>
  );
}

function RouteCard({
  tripType,
  isRoundTrip,
  from,
  to,
  fromIata,
  toIata,
  onClick,
  className,
}: {
  tripType: string;
  isRoundTrip: boolean;
  from: string;
  to: string;
  fromIata: string;
  toIata: string;
  onClick: () => void;
  className?: string;
}) {
  return (
    <Button
      variant="ghost"
      onClick={onClick}
      className={cn(
        "h-10 w-full min-w-0 justify-start overflow-hidden rounded-xl border border-transparent bg-transparent px-3  hover:border-border/70 hover:bg-muted/70 ",

        className,
      )}
    >
      <Badge>{tripType}</Badge>

      <div className="min-w-0 flex-1 lg:hidden">
        <p className="truncate text-sm font-semibold tracking-wide">
          {fromIata} {isRoundTrip ? "<->" : "->"} {toIata}
        </p>
      </div>

      <div className="hidden min-w-0 items-center gap-2 lg:flex">
        <span className="inline-flex min-w-0 items-center gap-1">
          <MapPin className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span className="truncate text-sm font-medium">{from}</span>
        </span>

        <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-primary">
          {isRoundTrip ? (
            <ArrowLeftRight className="h-3.5 w-3.5" />
          ) : (
            <ArrowRight className="h-3.5 w-3.5" />
          )}
        </span>

        <span className="inline-flex min-w-0 items-center gap-1">
          <MapPin className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span className="truncate text-sm font-medium">{to}</span>
        </span>
      </div>
    </Button>
  );
}
